import React, { useCallback } from 'react';
import { Platform } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import DropdownMenuItem from '@/components/DropdownMenu/DropdownMenuItem';
import DropdownMenuLabel from '@/components/DropdownMenu/DropdownMenuLabel';
import DropdownMenuItemDivider from '@/components/DropdownMenu/DropdownMenuItemDivider';
import SidebarListItemLayout from '@/components/Sidebar/SidebarListItemLayout';
import Icon from '@/components/Icon';
import {
  APP_STORE_URL,
  GOOGLE_PLAY_URL,
  MICROSOFT_STORE_URL,
  openStoreUrl,
} from '@/components/PublicFooter/publicFooterStoreLinks';

const isWeb = Platform.OS === 'web';

/**
 * "Get the Gab AI App" sidebar row that opens a small menu with the iOS,
 * Android, macOS, and Windows download links. Rendered in the logged-out sidebar
 * footer, just above the Documentation link.
 */
function SidebarGetAppDropdown() {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const router = useRouter();

  const handleIos = useCallback(() => openStoreUrl(APP_STORE_URL), []);
  const handleAndroid = useCallback(() => openStoreUrl(GOOGLE_PLAY_URL), []);
  const handleMacos = useCallback(() => router.push('/mac'), [router]);
  const handleWindows = useCallback(() => openStoreUrl(MICROSOFT_STORE_URL), []);

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <SidebarListItemLayout
          id="get-gab-app"
          title={t('sidebar.getApp')}
          icon="mobile-screen-button"
          noEllipsis
          rightIcon={
            <Icon name="chevron-down" size={10} color={colors.text_color_tertiary} />
          }
        />
      </DropdownMenu.Trigger>

      <DropdownMenu.Content
        className={isWeb ? 'DropdownMenuContent' : undefined}
        align="start"
        sideOffset={5}
      >
        {isWeb ? (
          <>
            <DropdownMenuLabel key="get-app-title" text={t('sidebar.getApp')} />
            <DropdownMenuItemDivider key="get-app-divider" />
            <DropdownMenuItem
              key="ios"
              onSelect={handleIos}
              icon="mobile-screen"
              iosIcon="iphone"
              title={t('sidebar.downloadIosApp')}
            />
            <DropdownMenuItem
              key="android"
              onSelect={handleAndroid}
              icon="mobile-screen-button"
              iosIcon="apps.iphone"
              title={t('sidebar.downloadAndroidApp')}
            />
            <DropdownMenuItem
              key="macos"
              onSelect={handleMacos}
              icon="desktop"
              title={t('sidebar.downloadMacosApp')}
            />
            <DropdownMenuItem key="windows" onSelect={handleWindows} icon="desktop"
              title={t('sidebar.downloadWindowsApp')} />
          </>
        ) : (
          <>
            <DropdownMenu.Item key="ios" onSelect={handleIos}>
              <DropdownMenu.ItemIcon ios={{ name: 'apple.logo', color: colors.text_color_primary }} />
              <DropdownMenu.ItemTitle>{t('sidebar.downloadIosApp')}</DropdownMenu.ItemTitle>
            </DropdownMenu.Item>
            <DropdownMenu.Item key="android" onSelect={handleAndroid}>
              <DropdownMenu.ItemIcon ios={{ name: 'square.and.arrow.down', color: colors.text_color_primary }} />
              <DropdownMenu.ItemTitle>{t('sidebar.downloadAndroidApp')}</DropdownMenu.ItemTitle>
            </DropdownMenu.Item>
            <DropdownMenu.Item key="macos" onSelect={handleMacos}>
              <DropdownMenu.ItemIcon ios={{ name: 'desktopcomputer', color: colors.text_color_primary }} />
              <DropdownMenu.ItemTitle>{t('sidebar.downloadMacosApp')}</DropdownMenu.ItemTitle>
            </DropdownMenu.Item>
            <DropdownMenu.Item key="windows" onSelect={handleWindows}>
              <DropdownMenu.ItemIcon ios={{ name: 'desktopcomputer', color: colors.text_color_primary }} />
              <DropdownMenu.ItemTitle>{t('sidebar.downloadWindowsApp')}</DropdownMenu.ItemTitle>
            </DropdownMenu.Item>
          </>
        )}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}

export default React.memo(SidebarGetAppDropdown);
